import { App, Divider, Dropdown, Space } from 'antd'
import Link from 'antd/es/typography/Link'
import React, { useMemo } from 'react'

import FunctionPermission from '@/components/permissions/function-permission'
import { FunctionKey } from '@/constants/function-key'
import { NormalStatus } from '@/constants/normal-status'
import { Employee } from '@/model/employee'
import {
  activateEmployee,
  deactivateEmployee,
  removeEmployee,
} from '@/redux/employee-actions'
import { selectMineFunctionKeys } from '@/redux/function-permission-selector'
import { useAppDispatch, useAppSelector } from '@/redux/utils'

export interface EmployeeOperationsProps {
  employee: Employee
  openUpdateFormModal: (employee: Employee) => void
}

export default function EmployeeOperations({ employee, openUpdateFormModal }: EmployeeOperationsProps) {
  const { modal, message } = App.useApp()
  const dispatch = useAppDispatch()
  const mineFunctionKeys = useAppSelector(selectMineFunctionKeys)

  const employeeMenuItems = useMemo(() => {
    const items = []
    if (mineFunctionKeys.includes(FunctionKey.EmployeesManageEnableDisableEmployee)) {
      items.push({
        key: employee.status === NormalStatus.Activated
          ? 'deactivate'
          : 'activate',
        label: employee.status === NormalStatus.Activated
          ? 'Deactivate'
          : 'Activate',
        onClick: () => {
          if (employee.status === NormalStatus.Activated) {
            void modal.confirm({
              icon: null,
              closeIcon: false,
              maskClosable: true,
              title: 'Deactivate Employee',
              content:
                "When you deactivate a Employee, your account will still be charged. To avoid unwanted fees, it's important to delete the account.",
              okText: 'Deactivate',
              onOk: async () => {
                await dispatch(
                  deactivateEmployee(employee.id),
                ).unwrap()
                void message.success('Deactivate success')
              },
            })
            return
          }
          void modal.confirm({
            icon: null,
            closeIcon: false,
            maskClosable: true,
            title: 'Activate Employee',
            content: `By reactivating ${employee.name}, employees will regain access to the account and its associated data`,
            okText: 'Activate',
            onOk: async () => {
              await dispatch(
                activateEmployee(employee.id),
              ).unwrap()
              void message.success('Activate success')
            },
          })
        },
      })
    }
    if (mineFunctionKeys.includes(FunctionKey.EmployeesManageDeleteEmployee)) {
      items.push({
        key: 'delete',
        label: 'Delete',
        danger: true,
        onClick: () => {
          void modal.confirm({
            icon: null,
            closeIcon: false,
            maskClosable: true,
            title: 'Delete Employee',
            okType: 'danger',
            okText: 'Delete',
            content: `Employee ${employee.name} will be removed. Any data related to this employee will be deleted and cannot be recovered.`,
            onOk: async () => {
              await dispatch(removeEmployee(employee.id)).unwrap()
              void message.success('Delete success')
            },
          })
        },
      })
    }
    return items
  }, [mineFunctionKeys, employee, modal, dispatch, message])

  return (
    <Space split={<Divider type="vertical" />}>
      <FunctionPermission functionKey={FunctionKey.EmployeesManageEditEmployee}>
        <Link
          onClick={() => {
            openUpdateFormModal(employee)
          }}
          disabled={employee.status === NormalStatus.Deactivated}
        >
          Edit
        </Link>
      </FunctionPermission>
      {employeeMenuItems.length > 0 &&
        <Dropdown menu={{ items: employeeMenuItems }}>
          <Link>More</Link>
        </Dropdown>
      }
    </Space>
  )
}
